import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from "react-router";
import { AuthProvider } from "@client/components/auth/AuthProvider";
import { RequireAdmin, RequireAuth } from "@client/components/auth/RouteGuards";
import { AmbientBackground } from "@client/components/layout/AmbientBackground";
import { SiteFooter } from "@client/components/layout/SiteFooter";
import { SiteHeader } from "@client/components/layout/SiteHeader";
import { usePageView } from "@client/hooks/use-page-view";
import { useScrollRestoration } from "@client/hooks/use-scroll-restoration";
import { HomePage } from "@client/pages/HomePage";
import { NotFoundPage } from "@client/pages/NotFoundPage";
import { ProductDetailPage } from "@client/pages/ProductDetailPage";

const STALE_TIME_MS = 60_000;

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: STALE_TIME_MS, refetchOnWindowFocus: false } },
});

function RootLayout() {
  usePageView();
  useScrollRestoration();
  return (
    <AuthProvider>
      <AmbientBackground />
      <div className="flex min-h-dvh flex-col">
        <SiteHeader />
        <div className="flex-1">
          <Outlet />
        </div>
        <SiteFooter />
      </div>
    </AuthProvider>
  );
}

/** v1 URLs that moved; the query string (e.g. ?redirect=) is preserved. */
function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

// Home and product pages ship in the main bundle (they are the landing pages); every other route
// is a separate chunk, so a first visit downloads less.
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <HomePage /> },
      { path: "/course/:slug", element: <ProductDetailPage /> },
      { path: "/blog", lazy: () => import("@client/pages/blog/BlogListPage").then((page) => ({ Component: page.BlogListPage })) },
      { path: "/blog/:slug", lazy: () => import("@client/pages/blog/ArticlePage").then((page) => ({ Component: page.ArticlePage })) },
      { path: "/topics/:slug", lazy: () => import("@client/pages/TopicPage").then((page) => ({ Component: page.TopicPage })) },
      { path: "/authors", lazy: () => import("@client/pages/authors/AuthorListPage").then((page) => ({ Component: page.AuthorListPage })) },
      { path: "/authors/:slug", lazy: () => import("@client/pages/authors/AuthorDetailPage").then((page) => ({ Component: page.AuthorDetailPage })) },
      { path: "/login", lazy: () => import("@client/pages/LoginPage").then((page) => ({ Component: page.LoginPage })) },
      { path: "/auth", element: <LegacyRedirect to="/login" /> },
      { path: "/my-library", element: <LegacyRedirect to="/library" /> },
      {
        element: <RequireAuth />,
        children: [
          { path: "/checkout/:slug", lazy: () => import("@client/pages/checkout/CheckoutPage").then((page) => ({ Component: page.CheckoutPage })) },
          { path: "/invoice/:id", lazy: () => import("@client/pages/checkout/InvoicePage").then((page) => ({ Component: page.InvoicePage })) },
          { path: "/invoices", lazy: () => import("@client/pages/checkout/InvoicesPage").then((page) => ({ Component: page.InvoicesPage })) },
          { path: "/library/:slug", lazy: () => import("@client/pages/library/LibraryProductPage").then((page) => ({ Component: page.LibraryProductPage })) },
          { path: "/library", lazy: () => import("@client/pages/library/LibraryPage").then((page) => ({ Component: page.LibraryPage })) },
          {
            element: <RequireAdmin />,
            children: [
              {
                path: "/admin",
                lazy: () => import("@client/components/admin/AdminLayout").then((layout) => ({ Component: layout.AdminLayout })),
                children: [
                  { index: true, lazy: () => import("@client/pages/admin/AdminDashboardPage").then((page) => ({ Component: page.AdminDashboardPage })) },
                  { path: "products", lazy: () => import("@client/pages/admin/AdminProductsPage").then((page) => ({ Component: page.AdminProductsPage })) },
                  { path: "products/:id", lazy: () => import("@client/pages/admin/AdminProductEditPage").then((page) => ({ Component: page.AdminProductEditPage })) },
                  { path: "authors", lazy: () => import("@client/pages/admin/AdminAuthorsPage").then((page) => ({ Component: page.AdminAuthorsPage })) },
                  { path: "authors/:id", lazy: () => import("@client/pages/admin/AdminAuthorEditPage").then((page) => ({ Component: page.AdminAuthorEditPage })) },
                  { path: "articles", lazy: () => import("@client/pages/admin/AdminArticlesPage").then((page) => ({ Component: page.AdminArticlesPage })) },
                  { path: "articles/:id", lazy: () => import("@client/pages/admin/AdminArticleEditPage").then((page) => ({ Component: page.AdminArticleEditPage })) },
                  { path: "coupons", lazy: () => import("@client/pages/admin/AdminCouponsPage").then((page) => ({ Component: page.AdminCouponsPage })) },
                  { path: "invoices", lazy: () => import("@client/pages/admin/AdminInvoicesPage").then((page) => ({ Component: page.AdminInvoicesPage })) },
                  { path: "invoices/:id", lazy: () => import("@client/pages/admin/AdminInvoiceDetailPage").then((page) => ({ Component: page.AdminInvoiceDetailPage })) },
                  { path: "users", lazy: () => import("@client/pages/admin/AdminUsersPage").then((page) => ({ Component: page.AdminUsersPage })) },
                  { path: "users/:id", lazy: () => import("@client/pages/admin/AdminUserDetailPage").then((page) => ({ Component: page.AdminUserDetailPage })) },
                ],
              },
            ],
          },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
